import React, { useEffect } from "react";
import { EnvelopeSimple, X } from "@phosphor-icons/react";

import { BetaBadge, NAVY } from "./WelcomeParts";
import { WELCOME_FEATURES, coverageSummary, type WelcomeFeature } from "./welcomeFeatures";

interface MobileWelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function MobileFeatureCard({ feature }: { feature: WelcomeFeature }) {
  const FeatureIcon = feature.icon;
  return (
    <div className="w-full bg-white border border-[rgba(52,52,52,0.15)] rounded-[10px] p-5 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <FeatureIcon size={22} color={NAVY} />
        <span className="font-cvcBody text-sm font-medium" style={{ color: NAVY }}>
          {feature.title}
        </span>
        <BetaBadge />
      </div>
      <p className="font-cvcBody text-sm leading-5 text-cvc-text mb-3">{feature.summary}</p>
      <ul className="font-cvcBody text-xs leading-5 text-cvc-text space-y-1 pl-4 list-disc">
        {feature.mobileBullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
      {/* The strategy tiles are desktop-only, so say what's covered here. */}
      <p className="font-cvcBody text-xs leading-5 text-cvc-text opacity-70 mt-3">
        {coverageSummary(feature)}
      </p>
    </div>
  );
}

function MobileWelcomeContent() {
  return (
    <div className="flex-1 overflow-y-auto" style={{ backgroundColor: "#F9F8F3" }}>
      <div className="flex flex-col items-center px-5 py-8">
        <h2
          className="font-cvcDisplay font-medium text-2xl leading-8 text-center mb-3"
          style={{ color: NAVY }}
        >
          Welcome to DealOS
        </h2>

        <p className="font-cvcBody text-sm leading-5 text-center mb-8 text-cvc-text">
          CVC&apos;s proprietary AI platform for deal intelligence and internal expertise, with
          more powerful features on the way.
        </p>

        {WELCOME_FEATURES.map((feature) => (
          <MobileFeatureCard key={feature.id} feature={feature} />
        ))}

        {/* Feedback link */}
        <a
          href="mailto:ai@cvc.com?subject=DealOS%20Feedback"
          className="flex items-center gap-1.5 text-xs font-cvcBody hover:underline mt-2 mb-4"
          style={{ color: NAVY }}
        >
          Submit feedback
          <EnvelopeSimple size={14} />
        </a>
      </div>
    </div>
  );
}

/* ─── Mobile full-screen modal ─── */

export default function MobileWelcomeModal({ isOpen, onClose }: MobileWelcomeModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 flex-shrink-0">
        <span className="text-base font-cvcBody" style={{ color: NAVY }}>
          DealOS
        </span>
        <button type="button" onClick={onClose} className="p-1" aria-label="Close">
          <X size={20} color={NAVY} />
        </button>
      </div>

      <div className="border-t border-[rgba(52,52,52,0.15)]" />

      <MobileWelcomeContent />

      {/* Get started button */}
      <div className="flex-shrink-0 px-5 py-4 bg-white border-t border-[rgba(52,52,52,0.15)]">
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 rounded-[8px] text-base font-cvcBody font-medium text-white transition-colors"
          style={{ backgroundColor: NAVY }}
        >
          Get started
        </button>
      </div>
    </div>
  );
}
